import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, finalize, Observable, Subject, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { completeSubject, defineUserType, promiseWithCatch, unsubscribeAll } from 'src/app/common/app.helpers';
import { ProfileFormTypes } from 'src/app/forms/forms.enum';
import { RoleInfo } from 'src/app/models/role-info.model';
import { DefaultService as RestApiService } from 'src/app/swagger-api/api/default.service';
import ProjectLinks from '../../common/project.links';
import { FullUser } from '../../swagger-api';
import { IndividualRoles, UserTypes, ViewProfileTypes } from './profile.enum';

@Injectable()
export class ProfileService implements OnDestroy {
  private user: FullUser;
  private userType: string = UserTypes.NONE;
  private individualUserRolesInfo: RoleInfo[] = [];

  private isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private profileUserChanges$$: Subject<ViewProfileTypes> = new Subject();
  private attributesMetadataChanges$$: Subject<boolean> = new Subject();

  private subscriptions$: Subscription[] = [];

  private apiService = inject(RestApiService);
  private sessionService = inject(SessionService);
  private router = inject(Router);

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
    completeSubject(this.profileUserChanges$$);
    completeSubject(this.attributesMetadataChanges$$);
  }

  public get isLoading$(): Observable<boolean> {
    return this.isLoading$$.asObservable();
  }

  public get profileUserChanges$(): Observable<ViewProfileTypes> {
    return this.profileUserChanges$$.asObservable();
  }

  public get metadataChanges$(): Observable<boolean> {
    return this.attributesMetadataChanges$$.asObservable();
  }

  public get userInfo(): FullUser {
    return this.user;
  }

  public get userRolesInfo(): RoleInfo[] {
    return this.individualUserRolesInfo;
  }

  public addUserRole(roleInfo: RoleInfo): void {
    this.individualUserRolesInfo.push(roleInfo);
  }

  public removeUserRole(roleInfo: RoleInfo): void {
    this.individualUserRolesInfo = this.individualUserRolesInfo.filter(
      (item: RoleInfo) => item.value !== roleInfo.value,
    );
  }

  public setupUserInfo(userId: string): void {
    this.fetchUserInfo(userId);
  }

  public fetchUserInfo(userId: string): void {
    const profileType: ViewProfileTypes =
      userId === this.sessionService.userId ? ViewProfileTypes.OWNER : ViewProfileTypes.REGULAR;

    this.isLoading$$.next(true);
    this.apiService
      .restUserGet(userId)
      .pipe(
        finalize(() => {
          this.isLoading$$.next(false);
        }),
      )
      .subscribe({
        next: (user: FullUser) => {
          this.initCurrentUser(user, defineUserType(user));
          this.profileUserChanges$$.next(profileType);
        },
        error: () => {
          if (profileType === ViewProfileTypes.OWNER) {
            promiseWithCatch(
              this.router
                .navigateByUrl(ProjectLinks.LINK_LOGIN, { skipLocationChange: true })
                .then(() => this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT)),
            );
          }
        },
      });
  }

  private initCurrentUser(user: FullUser, type: string): void {
    this.user = user;
    this.userType = type;

    this.fillIndividualUserRolesList();
    this.attributesMetadataChanges$$.next(true);
  }

  private fillIndividualUserRolesList(): void {
    this.individualUserRolesInfo = [];
    if (this.userType === ProfileFormTypes.INDIVIDUAL) {
      const individualRolesKeys: string[] = Object.keys(IndividualRoles);
      const userInfoKeys: string[] = Object.keys(this.user);

      userInfoKeys.forEach((key: string) => {
        const role: RoleInfo = this.individualUserRolesInfo.find((item: RoleInfo) => item.value === key);
        if (individualRolesKeys.includes(key) && !role) {
          this.addUserRole(new RoleInfo(key, IndividualRoles[key] as string, true, true));
        }
      });
    }
  }
}
